/**
 * AI conversation screen — shared vocabulary + diff engine.
 *
 * Ported from lilia-docs/design-handoffs/2026-06-19-ai-conversation-screen/
 * (`ai-data.jsx` + `ai-screen.jsx`) to our codebase, adapted to the real
 * `BlockOp` API contract (`api.ai.architect`) and the editor's typed-block
 * registry. The registry's `mark` codes mirror the block-mode left-lane marks
 * (StudioCardFlow TYPE_CODES) so the live skeleton reads like real blocks.
 */
import type { BlockOp } from "@/lib/api";

export type BlockCat = "meta" | "heading" | "prose" | "math" | "float" | "theorem" | "code";

export interface RegistryEntry {
  /** 2–3 char block-mode left-lane code (EQ / FIG / THM …). */
  mark: string;
  cat: BlockCat;
  name: string;
}

/** Block-type registry. Superset of the wizard's — keyed by the canonical
 *  editor block types the architect emits. `proof`/`lemma` share the theorem
 *  block; `title`/`authors` are conversation-only meta marks. */
export const REGISTRY: Record<string, RegistryEntry> = {
  title: { mark: "TI", cat: "meta", name: "Title" },
  authors: { mark: "AU", cat: "meta", name: "Authors" },
  abstract: { mark: "ABS", cat: "meta", name: "Abstract" },
  tableOfContents: { mark: "TOC", cat: "meta", name: "Table of contents" },
  heading: { mark: "H", cat: "heading", name: "Heading" },
  paragraph: { mark: "TXT", cat: "prose", name: "Paragraph" },
  list: { mark: "LST", cat: "prose", name: "List" },
  equation: { mark: "EQ", cat: "math", name: "Equation" },
  figure: { mark: "FIG", cat: "float", name: "Figure" },
  table: { mark: "TBL", cat: "float", name: "Table" },
  theorem: { mark: "THM", cat: "theorem", name: "Theorem" },
  proof: { mark: "PRF", cat: "theorem", name: "Proof" },
  lemma: { mark: "LEM", cat: "theorem", name: "Lemma" },
  code: { mark: "SRC", cat: "code", name: "Code" },
  bibliography: { mark: "BIB", cat: "meta", name: "Bibliography" },
};

/** Structured/semantic blocks carry the indigo accent; prose stays neutral. */
export const CAT_ACCENT: Record<BlockCat, boolean> = {
  meta: true,
  heading: true,
  math: true,
  float: true,
  theorem: true,
  code: true,
  prose: false,
};

export function registryFor(type: string): RegistryEntry {
  return REGISTRY[type] ?? { mark: type.slice(0, 3).toUpperCase(), cat: "prose", name: type };
}

// ── verify status (the differentiator — quiet "✓ compiles") ──────────────────
export type VerifyStatus = "pending" | "ok" | "warn";

// ── diff tags ────────────────────────────────────────────────────────────────
export type DiffTag = "same" | "add" | "edit" | "move" | "remove";

/** One block in the live skeleton — the editor's working IR for the AI screen.
 *  `uid` is a stable client id; `serverId` is set once the block exists in the
 *  document (so subsequent edit/move/remove ops can target it). */
export interface SkelBlock {
  uid: string;
  serverId?: string;
  type: string;
  /** Heading/title label (the bold line); null for body blocks. */
  label: string | null;
  /** Placeholder/summary line (italic) — never prose. */
  slot: string;
  /** Raw block payload content from the op — what gets persisted. Carried
   *  here so the apply step uses THIS block's content, not a re-matched op
   *  (the old id-match fell back to the first add op → duplicated blocks). */
  content?: Record<string, unknown>;
  diff?: DiffTag;
  oldLabel?: string | null;
  verify?: VerifyStatus;
}

/** Structural blocks that must appear at most once in a document. Guards
 *  against a model that over-generates (e.g. repeating the abstract). */
const SINGLETON_TYPES = new Set(["abstract", "tableOfContents", "bibliography"]);

let _uid = 0;
export function genUid(): string {
  return "ai" + ++_uid;
}

/** Best-effort human label + slot for a block payload `{type, content}`. */
function describeBlock(type: string, content: Record<string, unknown>): { label: string | null; slot: string } {
  const text = typeof content.text === "string" ? content.text : "";
  const caption = typeof content.caption === "string" ? content.caption : "";
  switch (type) {
    case "heading":
      return { label: text || registryFor(type).name, slot: "" };
    case "title":
      return { label: text || "Title", slot: "" };
    case "abstract":
      return { label: "Abstract", slot: text || "One-paragraph summary" };
    case "equation":
      return { label: null, slot: (content.latex as string) || "Equation" };
    case "figure":
      return { label: null, slot: caption || "Figure" };
    case "table":
      return { label: null, slot: "Table" };
    case "theorem": {
      const tt = (content.theoremType as string) || "theorem";
      return { label: null, slot: text || `${tt[0].toUpperCase()}${tt.slice(1)}` };
    }
    case "code":
      return { label: null, slot: (content.language as string) ? `Code (${content.language})` : "Code" };
    case "bibliography":
      return { label: null, slot: "References" };
    case "tableOfContents":
      return { label: null, slot: "Table of contents (auto-generated)" };
    case "list":
      return { label: null, slot: "List" };
    default:
      return { label: null, slot: text || registryFor(type).name };
  }
}

/** Find a block by its serverId, then by uid (proposed/uncommitted blocks). */
function indexOfTarget(list: SkelBlock[], id: string | undefined): number {
  if (!id) return -1;
  let i = list.findIndex((b) => b.serverId === id);
  if (i < 0) i = list.findIndex((b) => b.uid === id);
  return i;
}

/**
 * Apply a set of `BlockOp`s to the committed skeleton, producing a tagged
 * proposed list (each block carries a `diff` for the review UI). Mirrors the
 * design's `applyOps` but speaks the real op vocabulary (`afterId` anchors,
 * `block.{type,content}` payloads).
 */
export function applyOps(committed: SkelBlock[], ops: BlockOp[]): SkelBlock[] {
  const proposed: SkelBlock[] = committed.map((b) => ({
    ...b,
    diff: "same" as DiffTag,
    oldLabel: undefined,
  }));

  for (const op of ops) {
    if (op.op === "add" && op.block) {
      const blk = op.block;
      // Guard: never add a second singleton structural block (abstract / toc /
      // bibliography) — protects against a model that repeats a section.
      if (SINGLETON_TYPES.has(blk.type) && proposed.some((p) => p.type === blk.type)) {
        continue;
      }
      const { label, slot } = describeBlock(blk.type, blk.content ?? {});
      const nb: SkelBlock = {
        uid: op.id || genUid(),
        type: blk.type,
        label,
        slot,
        content: blk.content ?? {},
        diff: "add",
        verify: "pending",
      };
      if (op.afterId != null) {
        const i = indexOfTarget(proposed, op.afterId);
        proposed.splice(i >= 0 ? i + 1 : proposed.length, 0, nb);
      } else if (op.afterId === null) {
        proposed.unshift(nb);
      } else {
        proposed.push(nb);
      }
    } else if (op.op === "edit") {
      const i = indexOfTarget(proposed, op.id);
      if (i >= 0 && op.block) {
        const { label, slot } = describeBlock(op.block.type, op.block.content ?? {});
        proposed[i] = {
          ...proposed[i],
          diff: "edit",
          oldLabel: proposed[i].label,
          type: op.block.type,
          label,
          slot,
          content: op.block.content ?? {},
          verify: "pending",
        };
      }
    } else if (op.op === "remove") {
      const i = indexOfTarget(proposed, op.id);
      if (i >= 0) proposed[i] = { ...proposed[i], diff: "remove" };
    } else if (op.op === "move") {
      const i = indexOfTarget(proposed, op.id);
      if (i >= 0) {
        const [m] = proposed.splice(i, 1);
        m.diff = "move";
        const j = op.afterId == null ? 0 : indexOfTarget(proposed, op.afterId) + 1;
        proposed.splice(Math.max(0, j), 0, m);
      }
    }
  }
  return proposed;
}

/** Count the changed blocks in a proposed list. */
export function countChanges(proposed: SkelBlock[]): number {
  return proposed.filter((b) => b.diff && b.diff !== "same").length;
}

// ── empty / first-run suggestions ────────────────────────────────────────────
type Sug = { icon: "doc" | "shuffle" | "plus"; text: string };

/** Default starters (no kind chosen) — complete, sendable prompts. */
export const FIRST_RUN: Sug[] = [
  { icon: "doc", text: "Draft a research paper: abstract, introduction, methods, results, discussion, and references" },
  { icon: "plus", text: "Turn my rough notes into structured sections — I'll paste them next" },
  { icon: "shuffle", text: "Restructure this document into IMRaD" },
];

/** The document kinds the dropdown offers. */
export type DocKind = "paper" | "thesis" | "review" | "report" | "proposal" | "slides" | "problemset";

export const DOC_KINDS: { id: DocKind; label: string }[] = [
  { id: "paper", label: "Research paper" },
  { id: "thesis", label: "Thesis chapter" },
  { id: "review", label: "Literature review" },
  { id: "report", label: "Lab / technical report" },
  { id: "proposal", label: "Grant proposal" },
  { id: "slides", label: "Slides (beamer)" },
  { id: "problemset", label: "Problem set" },
];

/** Meaningful, complete starters per kind — each produces a usable scaffold. */
export const KIND_SUGGESTIONS: Record<DocKind, Sug[]> = {
  paper: [
    { icon: "doc", text: "Draft a research paper with abstract, introduction, methods, results, discussion, and references" },
    { icon: "plus", text: "Add a related-work section after the introduction" },
    { icon: "plus", text: "Add a results section with a data table and a figure" },
  ],
  thesis: [
    { icon: "doc", text: "Structure a thesis chapter: introduction, background, contribution, and summary" },
    { icon: "plus", text: "Add a methodology section with an algorithm block" },
    { icon: "plus", text: "Add a theorem with a proof sketch" },
  ],
  review: [
    { icon: "doc", text: "Outline a literature review grouped by theme, with a comparison table" },
    { icon: "plus", text: "Add a taxonomy section and a gaps / future-work section" },
    { icon: "plus", text: "Add a summary table of the key papers" },
  ],
  report: [
    { icon: "doc", text: "Set up a lab report: objective, method, results table, and conclusion" },
    { icon: "plus", text: "Add a figure with a caption and an appendix" },
    { icon: "plus", text: "Add an equations section for the analysis" },
  ],
  proposal: [
    { icon: "doc", text: "Draft a grant proposal: specific aims, significance, approach, and timeline" },
    { icon: "plus", text: "Add a budget-justification section" },
    { icon: "plus", text: "Add measurable objectives under the aims" },
  ],
  slides: [
    { icon: "doc", text: "Create a beamer talk: title, motivation, method, results, and conclusion" },
    { icon: "plus", text: "Add a results slide with a figure" },
    { icon: "plus", text: "Add a related-work slide" },
  ],
  problemset: [
    { icon: "doc", text: "Create a problem set with 5 questions and space for solutions" },
    { icon: "plus", text: "Add a proof-based question with hints" },
    { icon: "plus", text: "Add an equation-heavy applied problem" },
  ],
};

/** A real document title from the AI scaffold: first heading, else the first ask.
 *  Shared by the desktop + mobile architect so AI docs are never left "Untitled". */
export function deriveTitle(blocks: SkelBlock[], firstPrompt: string): string {
  const heading = blocks.find(
    (b) =>
      b.type === "heading" &&
      typeof (b.content as { text?: string })?.text === "string" &&
      ((b.content as { text?: string }).text ?? "").trim().length > 0,
  );
  if (heading) {
    const t = ((heading.content as { text?: string }).text ?? "").trim();
    return t.length > 120 ? t.slice(0, 120) : t;
  }
  const p = firstPrompt.trim();
  if (p) return p.length > 60 ? `${p.slice(0, 60)}…` : p;
  return "Untitled document";
}
